import { useEffect, useState } from "react";
import { EVIDENCE_BAR, LEAK_ROUTES, PROTOCOL_RULES, PROTOCOL_VERSION, REPLICATION_BAR, SOURCES } from "../../shared/protocol.ts";
import type { MeInfo } from "../lib/client.ts";
import { Icon } from "./Room.tsx";

interface Signoff { version: string; signedBy: string[]; players: { id: string; name: string }[] }

export function Protocol({ me }: { me: MeInfo }) {
  const [s, setS] = useState<Signoff | null>(null);
  useEffect(() => { void fetch("/api/signoff").then((r) => r.json()).then(setS); }, []);
  const sign = async () => {
    const r = await fetch("/api/signoff", { method: "POST" });
    const b = await r.json();
    setS((x) => (x ? { ...x, signedBy: b.signedBy } : x));
  };
  const iSigned = s?.signedBy.includes(me.id);
  return (
    <main className="doc">
      <p className="eyebrow">Protocol v{PROTOCOL_VERSION}</p>
      <h1 className="display">Set it up so you can't fool yourselves.</h1>
      <p className="lede">The record is rough. Once peeking was blocked, the controlled tests we could read showed no colour perception without touch. A few positive results exist, but they needed contact with the target. That doesn't settle your question. It does mean the test has to be tight enough that a positive result would mean something.</p>

      <section className="doc-section">
        <h2>Before your first test</h2>
        <ol className="steps">
          <li><b>Agree on this page.</b> You both sign below while nobody has a result to defend.</li>
          <li><b>Get a deep-cup blackout mask.</b> The workshop named Mindfold and Manta. A chin barrier or a hood sealed at the neck is stronger, and you'd want one before believing a positive result.</li>
          <li><b>Prop a spare phone at brow height or higher.</b> 30 to 40 cm from your face, screen toward you, out of your call camera's shot. It's your card.</li>
          <li><b>Run an eyes-open check.</b> 5 cards with the mask off, to prove capture and scoring work.</li>
          <li><b>Attack your rig.</b> 24 cards with the mask on, cheating as hard as you can. The workshop treats "there must be a hole in the mask" as a mental block to push past. This is the opposite approach: go find the hole. Test trials only count once the rig has held.</li>
          <li><b>Then run test turns.</b> 20 cards each, swapping, as often as you like.</li>
        </ol>
      </section>

      <section className="doc-section">
        <h2>The rules</h2>
        <div className="rules">
          {PROTOCOL_RULES.map((r, i) => (
            <article key={r.title} className="rule">
              <span className="rule-n">{String(i + 1).padStart(2, "0")}</span>
              <div><h3>{r.title}</h3><p>{r.why}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="doc-section">
        <h2>How the verdict works</h2>
        <p>The evidence score starts at 1 and acts like a running bet against pure chance. Hits above what chance predicts grow it, and misses shrink it. Under chance alone, the odds it ever reaches {EVIDENCE_BAR} are at most 1 in {EVIDENCE_BAR}, however often you look and whenever you stop. That's why you can check it after every session.</p>
        <p>Ian, Sydney and the two of you together are each tested, so the overall false-alarm risk is at most 3%. If a rig fails its attack, its trials are excluded and the bar doubles, because excluding them restarts the score. Reaching {EVIDENCE_BAR} starts the replication, where a fresh score on new trials has to reach {REPLICATION_BAR}.</p>
        <p>As a rough scale with four colours, where chance is 25%: a true rate of 50% takes about 40 trials, 40% takes about 100, 35% takes about 200, and 30% takes over 800. Those are simulated medians, and they assume a real effect exists.</p>
      </section>

      <section className="doc-section">
        <h2>Leak routes, worst first</h2>
        <table className="table">
          <thead><tr><th>Route</th><th>Fix</th><th>Who handles it</th></tr></thead>
          <tbody>
            {LEAK_ROUTES.map((l) => (
              <tr key={l.route}><td>{l.route}</td><td>{l.fix}</td><td className="nowrap">{l.handled === "app" ? "The app" : l.handled === "you" ? "You" : "Both"}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="hint">The order is a judgment call, not a measurement.</p>
      </section>

      <section className="doc-section">
        <h2>What this can't rule out</h2>
        <ul className="plain">
          <li><b>Determined cheating.</b> This is built for honest people fooling themselves. It wouldn't stop someone trying to deceive their partner, which is why the last step is an outside skeptic.</li>
          <li><b>A leak nobody thought of.</b> The rig attack only finds the leaks you try, and it's a screen rather than proof. Glow from a phone at close range through a mask is the one to watch.</li>
          <li><b>Screens being a different stimulus.</b> Paper reflects light, while a phone emits it. If a teacher objects that screens don't work, physical cards still get tracked, but they can't be blinded in the same way.</li>
          <li><b>Physical cards in general.</b> They can carry touch and heat cues, and hand shuffling isn't random. That's why they stay out of the verdict.</li>
        </ul>
      </section>

      <section className="doc-section">
        <h2>Sources</h2>
        <ul className="sources">
          {SOURCES.map((x) => <li key={x.url}><a href={x.url} target="_blank" rel="noreferrer">{x.label}</a> <span>{x.note}</span></li>)}
        </ul>
        <p className="hint">Press reports give no trial counts. Gardner's 1966 Science paper couldn't be read directly, only quotes of it.</p>
      </section>

      <section className="signoff" data-testid="signoff">
        <h2>Is this setup fair?</h2>
        <p>Sign now, before any data exists. If either of you changes something material, the protocol gets a new version and you both sign again.</p>
        <div className="sign-row">
          {s?.players.map((pl) => (
            <span key={pl.id} className="sign-name"><Icon kind={s.signedBy.includes(pl.id) ? "done" : "waiting"} /> {pl.name}: {s.signedBy.includes(pl.id) ? "signed" : "not signed"}</span>
          ))}
        </div>
        {!iSigned && <button className="btn primary big" onClick={sign} data-testid="sign">I agree this is a fair test</button>}
      </section>
    </main>
  );
}
